/**
 * EventBus event names used across the application.
 * Use these constants instead of plain strings to avoid typos
 * and enable IDE autocompletion.
 */
export const EVENT_BUS_EVENTS = Object.freeze({
  // Error handling events
  /** Fired when automation validation errors occur (403 with validation data) */
  STATUS_ERROR_CAPTURED: 'statusErrorCaptured',
  /** Fired on 404, 403, or 5xx errors requiring error page display */
  REQUEST_ERROR_CAPTURED: 'requestErrorCaptured',

  // vAI events
  /** Fired when backend errors occur during vAI record parsing */
  VAI_PARSE_EXISTING_RECORD_BACKEND_ERROR: 'vAIParseExistingRecordBackendError',
  /** Fired to prefill vAI chat message with content from universal search */
  VAI_MESSAGE_PREFILL: 'vAIMessagePrefill',
  /** Fired to open the vAI parse existing record modal with recordId and fileId */
  OPEN_PARSE_EXISTING_RECORD_MODAL: 'openParseExistingRecordModal',
  /** Fired when vAI sidebar is closed */
  VAI_SIDEBAR_CLOSED: 'vAISidebarClosed',
  /** Fired when active chat changes in vAI sidebar */
  VAI_ACTIVE_CHAT_CHANGED: 'vAIActiveChatChanged',
  /** Fired to open the vAI sidebar to a specific conversation by UUID */
  VAI_OPEN_CHAT: 'vAIOpenChat',
  /** Fired to create a new vAI chat with a specific initiation context (e.g. chat template) */
  VAI_CREATE_NEW_CHAT_WITH_CONTEXT: 'vAICreateNewChatWithContext',

  // Notification events
  /** Fired when all notifications are marked as read */
  NOTIFICATIONS_MARK_ALL: 'notificationsMarkAll',
  /** Fired to refresh the notifications widget */
  ACTIVITY_STREAM_LOAD: 'activityStreamLoad',
  /** Fired after loading more activity stream messages with pagination info */
  ACTIVITY_STREAM_LOAD_MORE: 'activityStreamLoadMore',

  // Record events
  /** Fired when record status is updated */
  RECORD_STATUS_CHANGED: 'recordStatusChanged',
  /** Fired to refresh linked field tables and object custom field tables */
  REFRESH_LOADED_TABLES: 'refreshLoadedTables',

  // Activity stream events
  /** Fired to scroll the activity stream to top */
  SCROLL_TO_TOP: 'scrollToTop',
  /** Fired when user clicks reply on a message in activity stream */
  REPLY_TO_MESSAGE: 'replyToMessage',
  /** Fired when PDF file is ready to post to activity stream message */
  POST_PDF_TO_ADD_MESSAGE: 'postPdfToAddMessage',

  // Document events
  /** Fired to trigger batch download of document group */
  DOC_ITEM_DOWNLOAD_GROUP: 'docItemDownloadGroup',
} as const)

export type EventBusEvent = (typeof EVENT_BUS_EVENTS)[keyof typeof EVENT_BUS_EVENTS]
